import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Location } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, IGetRowsParams } from 'ag-grid-community';
import { CopyCellRendererComponent } from 'src/app/components/cellrenders/copy-cell-renderer-component';
import { ClientsService } from 'src/app/services/clients.service';
import { LanguageService } from 'src/app/services/language.service';
import { QuestionnaireRendererComponent } from 'src/app/components/cellrenders/questionnaire-renderer-component';
import { SatisfactionCellRendererComponent } from 'src/app/components/cellrenders/satisfaction-cell-renderer-component';
import { AdminService } from 'src/app/services/admin.service';

@Component({
  selector: 'app-questionnaires-dashboard',
  templateUrl: './questionnaires-dashboard.component.html',
  styleUrl: './questionnaires-dashboard.component.scss'
})
export class QuestionnairesDashboardComponent implements OnInit {
  loading = false;
  public locale = this.languageService.getLanguage();
  protected activeTab = 'all';
  protected activeSatisfactionLevelTab = 'all';
  public totalQuestionnaires: number = 0;
  public overviewQnnairesAnalytics: any = {};
  constructor(private clientsService: ClientsService,
    private languageService: LanguageService,
    private router: Router,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private adminService: AdminService
  ) { }


  gridApi!: GridApi;
  @ViewChild('agGrid') agGrid!: AgGridAngular;
  dataSource: any;


  public statuses = {
    all: $localize`Todos`,
    initial: $localize`Inicial`,
    followUp: $localize`Seguimiento`,
    athletes: $localize`Alto rendimiento deportivo`
  };
  statusEntries = Object.entries(this.statuses);

  public satisfactionLevels = {
    all: $localize`Todos`,
    positive: $localize`Positivo`,
    neutral: $localize`Neutral`,
    negative: $localize`Negativo`
  }
  satisfactionLevelFilters = Object.entries(this.satisfactionLevels);

  rowStyle = (params: any) => {
    const classes = {};
    if (params.data?.is_reviewed) {
      classes['backgroundColor'] = '#d4edda'; // Verde claro
    } else {
      classes['backgroundColor'] = '#f8d7da'; // Rojo claro
    }

    if (params.data?.is_vip_client) {
      classes['backgroundColor'] = '#B8860B';
    }
    return classes;
  }

  tabChangeSatisfactionLevel(tab: string) {
    this.activeSatisfactionLevelTab = tab;
    this.getAnsweredQuestionnaires();
    this.storeHistory();
  }

  tabChange(tab: string) {
    this.activeTab = tab;
    this.getAnsweredQuestionnaires();
    this.storeHistory();
  }

  columnDefs: ColDef[] = [
    { field: 'questionary_name', headerName: 'Questionnaire', 
      valueGetter: (params) => params.data, 
      minWidth: 150, sortable: false, cellRenderer: QuestionnaireRendererComponent},
    { field: 'user_id', headerName: 'User ID', sortable: false, 
      maxWidth: 130, cellRenderer: CopyCellRendererComponent },
    { field: 'name', headerName: 'Name', 
      valueGetter: (params) => `${params.data?.name?.toUpperCase()} ${params.data?.lastname?.toUpperCase()}`,
      cellRenderer: CopyCellRendererComponent, sortable: false },
    { field: 'email', headerName: 'Email', resizable: true, cellRenderer: CopyCellRendererComponent, sortable: false},
    { field: 'satisfaction_level', 
      headerName: $localize`Nivel de Satisfacción`,  
      cellRenderer: SatisfactionCellRendererComponent,
    },
    { field: 'last_protocol_name_en', sortable: false, headerName: 'Last Protocol', valueGetter: (params) => params.data?.last_protocol_name_en?.toUpperCase() },
    { field: 'coach_username', headerName: 'Coach', sortable: false },
    { field: 'manager_username', headerName: 'Manager', sortable: false },
    { field: 'is_reviewed', headerName: 'Reviewed', maxWidth: 130, sortable: false},
    { field: 'created_at', headerName: 'Answered At', valueGetter: (params) => params.data?.created_at ? this.formatDate(params.data?.created_at) : '', cellRenderer: CopyCellRendererComponent, sortable: false },
    { field: 'reviewed_at', headerName: 'Reviewed At', valueGetter: (params) => params.data?.reviewed_at && params.data?.is_reviewed ? this.formatDate(params.data?.reviewed_at) : '', cellRenderer: CopyCellRendererComponent, sortable: false },
  ]
  ngOnInit() {
    
    if (Object.keys(this.activatedRoute.snapshot.queryParams).length) {
      if (this.activatedRoute.snapshot.queryParams?.usersTab) {
        this.activeTab = this.activatedRoute.snapshot.queryParams?.usersTab
        this.activeSatisfactionLevelTab = this.activatedRoute.snapshot.queryParams?.satisfactionLevelFilter
      }
    }
    this.getAnsweredQuestionnaires();
    this.getOverviewAnalyticsQnnaires();
  }

  last12Months = Array.from({ length: 12 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - i);

    const earliest = new Date(2024, 11); // Diciembre 2024 (mes 11)
    if (date < earliest) return null;

    return {
      value: date.toISOString().slice(0, 7), // 'YYYY-MM'
      label: date.toISOString().slice(0, 7)
    };
  }).filter(Boolean);

  onChangeMonth(event: Event) {
    const month = (event.target as HTMLSelectElement).value;
    this.getOverviewAnalyticsQnnaires(month); // tu método para hacer el request
  }

  getOverviewAnalyticsQnnaires(month: string = '') {
    this.adminService.getOverviewAnalyticsFollowUpQnnaires(month).subscribe({
      next: (response) => {
        this.overviewQnnairesAnalytics = response;
        console.log(response);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  formatDate(date: string | Date) {
    return new Date(date).toLocaleDateString(this.locale, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getAnsweredQuestionnaires() {
    this.dataSource = {
      getRows: (params: IGetRowsParams) => {
        this.loading = true;
        const startRow = params.startRow; // Índice inicial de la página
        const endRow = params.endRow; // Índice final de la página
        const pageSize = endRow - startRow; // Filas por página (puede variar)
        const currentPage = Math.floor(startRow / pageSize); // Página actual

        this.clientsService.getDashboardAnswerGroups(currentPage + 1, pageSize, this.activeTab, this.activeSatisfactionLevelTab).subscribe((response) => {
          params.successCallback(response.data, response.total);
          this.totalQuestionnaires = response.total;
          this.loading = false;
        }, (error) => {
          console.error(error);
          params.failCallback();
          this.loading = false;
        });
      }
    }
  }


  onRowClicked(event) {
   this.router.navigate(['/admin/clients/', event.data.user_id, 'answer-groups', event.data.id, '.']); 
  }

  storeHistory() {
    let queryParams: Params | null = {
      usersTab: this.activeTab,
      satisfactionLevelFilter: this.activeSatisfactionLevelTab,
    }
    const urlTree = this.router.createUrlTree([], {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
      preserveFragment: true
    });
    this.location.go(urlTree.toString())
  }
}
